import {
    select as D3select,
    arc as D3arc,
    pie as D3pie
} from 'd3';

/*
small: bool, small gauges get a label under the gauge, large gauges get a label inside
colors: a D3scaleOrdinal() to map key to color, if empty will use theme band color
size: gauge will resize based on this value. can be set by the caller on resize for example.

multi gauge mode:
    data: {key, label, value, series: [key, value]}
    will create a pie from all values totalling 100%, top level value is used for the gauge label

single gauge mode:
    key: can map to colors e.g. severity
    severities: threshold map { yellow: 80, red: 100 } - order matters
    value: will be treated as a percentage. e.g., 50
*/
const D3Gauge = function () {
    let size = 500;
    let small = false;
    let single = false;
    let valueLabel;
    let colors;
    let theme;

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            const thicknessFactor = 0.00060;
            const radius = size * 0.9 / 2;
            const chopOff = 2 * Math.PI / 3;
            const cx = size / 2;
            const cy = size / 2;

            const arc = D3arc()
                .outerRadius(radius)
                .innerRadius(radius - thicknessFactor * size);

            const keyArc = D3arc()
                .outerRadius(radius + 2)
                .innerRadius(radius - thicknessFactor * size - 2);

            const pie = D3pie().value(d => d.value)
                .startAngle(-(Math.PI - chopOff / 2))
                .endAngle(Math.PI - chopOff / 2);

            // add the parent once
            let g = D3select(this).selectAll('.dialgauge').data(['once']);
            g = g.enter()
                .append('g').attr('class', 'dialgauge')
                .merge(g);

            let segments = data.series || [];

            renderGauge();
            renderText();

            function renderGauge () {
                if (single) {
                    // make a fake pie out of our single value
                    segments = [
                        { key: data.key, value: Math.round((data.value * 100) / 100) },
                        { key: 'background-circle', value: 100 - Math.round((data.value * 100) / 100) }
                    ];
                    pie.sort(null);
                }

                // this is essentially a thin donut chart
                const pdata = pie(segments);
                // add in reverse order so that the segments are laid with left on top
                const pjoin = g.selectAll('path').data(pdata.reverse());
                pjoin.enter().append('path')
                    .attr('d', arc)
                    .merge(pjoin)
                    .on('mouseover', function (e, d) {
                        if (single || data.lock) return;
                        D3select(this)
                            .transition()
                            .duration(theme.transitions.duration.short)
                            .attr('d', keyArc);
                        data.sel = d.data;
                        // bring to front - thank you d3 v5
                        D3select(this).raise();
                        renderText();
                    })
                    .on('mouseout', function (e, d) {
                        if (single || data.lock) return;
                        D3select(this)
                            .transition()
                            .duration(theme.transitions.duration.short)
                            .attr('d', arc);
                        data.sel = undefined;
                        renderText();
                    })
                    .on('click', function (e, d, i) {
                        if (single) return;
                        data.lock = data.lock !== d.data.key ? d.data.key : undefined;
                        data.sel = d.data;
                        // redraw
                        renderGauge();
                        renderText();
                    })
                    .attr('class', d => d.data.key === 'background-circle' ? 'circle' : 'band value')
                    .style('fill', function (d) {
                        return d.data.key === 'background-circle' ? D3select(this).style('color')
                            : colors ? colors(d.data.key) : D3select(this).style('color');
                    })
                    .style('stroke', function (d) {
                        return d.data.key === 'background-circle' ? D3select(this).style('color')
                            : (colors ? colors(d.data.key) : D3select(this).style('color'));
                    })
                    .attr('transform', `translate(${cx}, ${cy})`)
                    .each(function (d) {
                        // raise (bring to front) if it's selected before redrawing the segment
                        if (data.sel && data.sel.key === d.data.key) {
                            D3select(this).raise();
                        }
                        D3select(this).attr('d', data.sel && data.sel.key === d.data.key ? keyArc(d) : arc(d));
                    });
            }

            function renderText () {
                if (segments.length <= 0) return;

                let fs = small ? Math.round(size / 5) : Math.round(size / 10);
                const label = g.selectAll('text.gauge-label').data(['once']);
                label.enter().append('text')
                    .attr('class', 'gauge-label')
                    .merge(label)
                    .attr('x', cx)
                    .attr('y', small ? size - fs : cy / 2 + fs / 2)
                    .attr('dy', fs / 2)
                    .attr('text-anchor', 'middle')
                    .attr('class', 'text gauge-label')
                    .style('font-size', fs + 'px')
                    .style('stroke-width', '0px')
                    .text(data.label);
                label.exit().remove();

                // if valueLabel passed then use that unmodified
                // otherwise default to first guy on the list
                const value = valueLabel || vlFormat(data.sel ? data.sel.value : segments[0].value);
                const key = data.sel ? data.sel.key : segments[0].key;

                // add and update the value label
                fs = small ? Math.round(size / 5) : value.length > 10 ? Math.round(size / 7) : Math.round(size / 6);
                const dlabel = g.selectAll('text.val-label').data(['once']);
                dlabel.enter().append('text')
                    .attr('class', 'val-label text')
                    .merge(dlabel)
                    .attr('x', cx)
                    .attr('y', small ? cy - fs / 2 : size - cy / 2 - fs)
                    .attr('dy', small ? fs / 1.5 : fs / 2)
                    .attr('text-anchor', 'middle')
                    .style('font-size', fs + 'px')
                    .style('stroke-width', '0px')
                    .style('fill', function (d) {
                        return colors ? colors(key) : D3select(this).style('color');
                    })
                    .text(value);
                dlabel.exit().remove();

                function vlFormat (str) {
                    return (isNaN(str) ? 0 : Math.round(str * 100) / 100) + (single ? '%' : '');
                }
            }
        });
    }

    my.size = function (v) {
        if (!arguments.length) return size;
        size = v;
        return my;
    };

    my.colors = function (v) {
        if (!arguments.length) return colors;
        colors = v;
        return my;
    };

    my.single = function (v) {
        if (!arguments.length) return single;
        single = v;
        return my;
    };

    my.small = function (v) {
        if (!arguments.length) return small;
        small = v;
        return my;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    my.valueLabel = function (v) {
        if (!arguments.length) return valueLabel;
        valueLabel = v;
        return my;
    };

    return my;
};

export default D3Gauge;
